import { useState, useEffect } from '@wordpress/element';
import apiFetch from '@wordpress/api-fetch';
import { Select, ConfirmDialog, useConfirm } from '../ui';

const PincodeList = ({ onEdit, toast }) => {
    const [pincodes, setPincodes]         = useState([]);
    const [currentPage, setCurrentPage]   = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [loading, setLoading]           = useState(false);
    const { dialog, confirm, handleConfirm, handleCancel } = useConfirm();

    const fetchPincodes = () => {
        const norm = (d) => Array.isArray(d) ? d : (typeof d === 'object' && d ? Object.values(d) : []);
        setLoading(true);
        apiFetch({ path: `cpiw/v1/getpincode?nocache=${Date.now()}` })
            .then(d => { setPincodes(norm(d)); setLoading(false); })
            .catch(err => { console.error(err); setLoading(false); });
    };

    useEffect(() => { fetchPincodes(); }, []);

    const handleDelete = async (id) => {
        const isAll = id === 'all';
        const ok = await confirm({
            title:        isAll ? 'Delete all pincodes?' : 'Delete this pincode?',
            message:      isAll
                ? 'This will permanently remove every pincode entry. This cannot be undone.'
                : 'This pincode will be permanently removed.',
            confirmLabel: isAll ? 'Delete All' : 'Delete',
            type:         'danger',
        });
        if (!ok) return;

        const req = isAll
            ? { path: 'cpiw/v1/deletepincode/all', method: 'DELETE' }
            : { path: `cpiw/v1/deletepincode/${id}`, method: 'DELETE' };

        setLoading(true);
        apiFetch(req)
            .then(() => {
                setLoading(false);
                toast.success(isAll ? 'All pincodes deleted.' : 'Pincode deleted.');
                fetchPincodes();
            })
            .catch((err) => { console.error(err); setLoading(false); toast.error('Failed to delete.'); });
    };

    const indexOfLast  = currentPage * itemsPerPage;
    const indexOfFirst = indexOfLast - itemsPerPage;
    const currentItems = pincodes.slice(indexOfFirst, indexOfLast);
    const totalPages   = Math.ceil(pincodes.length / itemsPerPage);
    const codCount     = pincodes.filter(p => p.caseondilvery == 1).length;

    return (
        <div className="plugin-pincode-list-page">
            <ConfirmDialog dialog={dialog} onConfirm={handleConfirm} onCancel={handleCancel} />

            {loading && (
                <div className="cpiw-overlay-loader">
                    <div className="cpiw-spinner-wrap">
                        <div className="cpiw-spinner" />
                        <span className="cpiw-spinner-label">Loading…</span>
                    </div>
                </div>
            )}

            {/* Stats */}
            <div className="cpiw-stats-row">
                <div className="cpiw-stat-card">
                    <div className="cpiw-stat-card__icon cpiw-stat-card__icon--brand">📍</div>
                    <div><div className="cpiw-stat-card__value">{pincodes.length}</div><div className="cpiw-stat-card__label">Total Pincodes</div></div>
                </div>
                <div className="cpiw-stat-card">
                    <div className="cpiw-stat-card__icon cpiw-stat-card__icon--accent">💳</div>
                    <div><div className="cpiw-stat-card__value">{codCount}</div><div className="cpiw-stat-card__label">COD Available</div></div>
                </div>
                <div className="cpiw-stat-card">
                    <div className="cpiw-stat-card__icon cpiw-stat-card__icon--warn">📄</div>
                    <div><div className="cpiw-stat-card__value">{totalPages || 0}</div><div className="cpiw-stat-card__label">Pages</div></div>
                </div>
            </div>

            {/* Main card */}
            <div className="cpiw-card">
                <div className="cpiw-card__header">
                    <div className="cpiw-card__header-icon"><span style={{ fontSize: '16px' }}>📋</span></div>
                    <div style={{ flex: 1 }}>
                        <div className="cpiw-card__title">Pincode List</div>
                        <div className="cpiw-card__desc">Manage your serviceable delivery zones</div>
                    </div>
                    <button className="cpiw-btn-danger" onClick={() => handleDelete('all')}>🗑 Delete All</button>
                </div>

                <div className="cpiw-card__body">
                    {/* Toolbar */}
                    <div className="cpiw-table-toolbar">
                        <div className="cpiw-table-toolbar__left">
                            <span className="cpiw-perpage-label">Show</span>
                            <Select
                                value={itemsPerPage}
                                options={[5,10,20,50,100].map(n => ({ label: String(n), value: n }))}
                                onChange={(val) => { setItemsPerPage(parseInt(val)); setCurrentPage(1); }}
                            />
                            <span className="cpiw-perpage-label">entries</span>
                        </div>
                        <div className="cpiw-table-toolbar__right">
                            <span className="cpiw-pagination__info">
                                Showing <strong>{pincodes.length > 0 ? indexOfFirst + 1 : 0}–{Math.min(indexOfLast, pincodes.length)}</strong> of <strong>{pincodes.length}</strong>
                            </span>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="cpiw-table-wrap">
                        <table className="cpiw-table">
                            <thead>
                                <tr>
                                    <th>Pincode</th><th>City</th><th>State</th>
                                    <th>Delivery Days</th><th>Shipping ₹</th><th>COD</th><th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentItems.length > 0 ? currentItems.map(item => (
                                    <tr key={item.id}>
                                        <td>{item.pincode}</td>
                                        <td>{item.city}</td>
                                        <td>{item.state}</td>
                                        <td>{item.ddate}</td>
                                        <td>{item.ship_amount}</td>
                                        <td>
                                            {item.caseondilvery == 1
                                                ? <span className="cpiw-badge cpiw-badge--yes">✓ Yes</span>
                                                : <span className="cpiw-badge cpiw-badge--no">✕ No</span>
                                            }
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', gap: '6px' }}>
                                                <button className="cpiw-btn cpiw-btn--sm" onClick={() => onEdit(item.id)}>✎ Edit</button>
                                                <button className="cpiw-btn-danger cpiw-btn--sm" onClick={() => handleDelete(item.id)}>🗑</button>
                                            </div>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr className="cpiw-empty-row">
                                        <td colSpan="7">
                                            <span className="cpiw-empty-icon">📭</span>
                                            No pincodes found. Add your first pincode using the <strong>Add Pincode</strong> tab.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="cpiw-pagination">
                        <span className="cpiw-pagination__info">
                            Page <strong>{currentPage}</strong> of <strong>{totalPages || 1}</strong>
                        </span>
                        <div className="cpiw-pagination__controls">
                            <button className="cpiw-page-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(1)}>« First</button>
                            <button className="cpiw-page-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>‹ Prev</button>
                            <span className="cpiw-page-current">{currentPage} / {totalPages || 1}</span>
                            <button className="cpiw-page-btn" disabled={currentPage >= totalPages || !totalPages} onClick={() => setCurrentPage(p => p + 1)}>Next ›</button>
                            <button className="cpiw-page-btn" disabled={currentPage >= totalPages || !totalPages} onClick={() => setCurrentPage(totalPages)}>Last »</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PincodeList;
